import { ESIGN_TOOL_SLUGS, ESIGN_TOOL_TYPES } from './toolIds';
import type { UnlayerDesignRecord } from './types';

type UnlayerDesign = UnlayerDesignRecord;

export interface StripEsignToolsResult {
  design: UnlayerDesign;
  removedCount: number;
  removedTypes: string[];
}

function isEsignContentBlock(record: Record<string, unknown>): boolean {
  const type = typeof record.type === 'string' ? record.type : '';
  const slug = typeof record.slug === 'string' ? record.slug : '';
  const name = typeof record.name === 'string' ? record.name : '';
  return ESIGN_TOOL_TYPES.has(type) || ESIGN_TOOL_SLUGS.has(slug) || ESIGN_TOOL_SLUGS.has(name);
}

function stripFromBody(body: unknown): {
  body: unknown;
  removedCount: number;
  removedTypes: Set<string>;
} {
  const removedTypes = new Set<string>();
  let removedCount = 0;

  if (!body || typeof body !== 'object') {
    return { body, removedCount, removedTypes };
  }

  const bodyRecord = body as Record<string, unknown>;
  const rows = bodyRecord.rows;
  if (!Array.isArray(rows)) {
    return { body, removedCount, removedTypes };
  }

  const nextBody = {
    ...bodyRecord,
    rows: rows
      .map((row) => {
        if (!row || typeof row !== 'object') {
          return row;
        }
        const rowRecord = row as Record<string, unknown>;
        const columns = rowRecord.columns;
        if (!Array.isArray(columns)) {
          return row;
        }
        return {
          ...rowRecord,
          columns: columns.map((column) => {
            if (!column || typeof column !== 'object') {
              return column;
            }
            const columnRecord = column as Record<string, unknown>;
            const contents = columnRecord.contents;
            if (!Array.isArray(contents)) {
              return column;
            }
            const nextContents = contents.filter((content) => {
              if (!content || typeof content !== 'object') {
                return true;
              }
              const block = content as Record<string, unknown>;
              if (isEsignContentBlock(block)) {
                removedCount += 1;
                const type = block.type;
                if (typeof type === 'string' && ESIGN_TOOL_TYPES.has(type)) {
                  removedTypes.add(type);
                } else if (typeof block.slug === 'string') {
                  removedTypes.add(block.slug);
                } else if (typeof block.name === 'string') {
                  removedTypes.add(block.name);
                }
                return false;
              }
              return true;
            });
            return {
              ...columnRecord,
              contents: nextContents,
            };
          }),
        };
      })
      .filter((row) => {
        if (!row || typeof row !== 'object') {
          return true;
        }
        const columns = (row as Record<string, unknown>).columns;
        if (!Array.isArray(columns)) {
          return true;
        }
        return columns.some((column) => {
          if (!column || typeof column !== 'object') {
            return false;
          }
          const contents = (column as Record<string, unknown>).contents;
          return Array.isArray(contents) && contents.length > 0;
        });
      }),
  };

  return { body: nextBody, removedCount, removedTypes };
}

function getRowsFromDesignRoot(design: Record<string, unknown>): unknown[] | undefined {
  const body = design.body;
  if (body && typeof body === 'object') {
    const rows = (body as Record<string, unknown>).rows;
    if (Array.isArray(rows)) {
      return rows;
    }
  }
  const rows = design.rows;
  return Array.isArray(rows) ? rows : undefined;
}

function resolveDesignRoot(content: unknown): unknown {
  if (!content || typeof content !== 'object') {
    return content;
  }
  const record = content as Record<string, unknown>;
  if (record.design && typeof record.design === 'object' && !Array.isArray(record.design)) {
    return record.design;
  }
  return content;
}

function countEsignBlocksInDesign(design: unknown): number {
  if (!design || typeof design !== 'object') {
    return 0;
  }

  const rows = getRowsFromDesignRoot(design as Record<string, unknown>);
  if (!rows) {
    return 0;
  }

  let count = 0;
  for (const row of rows) {
    if (!row || typeof row !== 'object') {
      continue;
    }
    const columns = (row as Record<string, unknown>).columns;
    if (!Array.isArray(columns)) {
      continue;
    }
    for (const column of columns) {
      if (!column || typeof column !== 'object') {
        continue;
      }
      const contents = (column as Record<string, unknown>).contents;
      if (!Array.isArray(contents)) {
        continue;
      }
      for (const content of contents) {
        if (
          content &&
          typeof content === 'object' &&
          isEsignContentBlock(content as Record<string, unknown>)
        ) {
          count += 1;
        }
      }
    }
  }

  return count;
}

/** True when the design has at least one e-sign block in body.rows. */
export function designContainsEsignTool(design: unknown): boolean {
  return countEsignBlocksInDesign(resolveDesignRoot(design)) > 0;
}

/** Count e-sign blocks (proposal/custom/contract send validation). */
export function countEsignContentBlocks(design: unknown): number {
  return countEsignBlocksInDesign(resolveDesignRoot(design));
}

/**
 * Returns true when document content (design wrapper or raw body) has e-sign blocks.
 */
export function contentContainsEsignTool(content: unknown): boolean {
  if (!content || typeof content !== 'object') {
    return false;
  }

  const record = content as Record<string, unknown>;
  const design = record.design;
  if (design && typeof design === 'object' && !Array.isArray(design)) {
    return designContainsEsignTool(design);
  }

  if (record.body && typeof record.body === 'object') {
    return designContainsEsignTool(record);
  }

  return false;
}

/**
 * Returns true when any paginated page design has e-sign blocks.
 * Scans `pages[].design` only — does not inspect root document content.
 */
export function pagesContainEsignTool(pages: unknown): boolean {
  if (!Array.isArray(pages)) {
    return false;
  }

  for (const page of pages) {
    if (!page || typeof page !== 'object') {
      continue;
    }
    const design = (page as Record<string, unknown>).design;
    if (designContainsEsignTool(design)) {
      return true;
    }
  }

  return false;
}

/**
 * Removes e-sign custom tool blocks from an Unlayer design (contract template hygiene).
 */
export function stripEsignToolsFromDesign(design: UnlayerDesign): StripEsignToolsResult {
  if (!design || typeof design !== 'object') {
    return { design, removedCount: 0, removedTypes: [] };
  }

  const designRecord = design as Record<string, unknown>;
  const body = designRecord.body;
  const hasBodyRows = Boolean(
    body && typeof body === 'object' && Array.isArray((body as Record<string, unknown>).rows)
  );
  const hasRootRows = Array.isArray(designRecord.rows);
  if (!hasBodyRows && !hasRootRows) {
    return { design, removedCount: 0, removedTypes: [] };
  }

  const stripTarget = hasBodyRows ? body : designRecord;
  const { body: strippedTarget, removedCount, removedTypes } = stripFromBody(stripTarget);
  const removedTypeList = [...removedTypes];

  return {
    design: hasBodyRows ? { ...design, body: strippedTarget } : (strippedTarget as UnlayerDesign),
    removedCount,
    removedTypes: removedTypeList,
  };
}

/**
 * Strips e-sign tools from document content and returns design-only friendly shape.
 */
export function stripEsignToolsFromContent(
  content: Record<string, unknown>
): Record<string, unknown> {
  const design = content.design;
  if (design && typeof design === 'object' && !Array.isArray(design)) {
    const stripped = stripEsignToolsFromDesign(design as UnlayerDesign);
    return {
      design: stripped.design,
    };
  }

  if (content.body && typeof content.body === 'object') {
    const stripped = stripEsignToolsFromDesign(content as UnlayerDesign);
    return {
      design: stripped.design,
    };
  }

  return { design: stripEsignToolsFromDesign({}).design };
}
